import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, ArrowRight, CalendarX2, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import { getSwapEligibleShifts, getSwappableColleagues, SwapColleague } from '../../../../api/Attendance/shiftSwap';
import { ScheduledShiftView } from '../../../../types/scheduling';
import { ShiftSwapRequestInput } from '../../../../models/shiftSwap';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { formatLocalShiftSummary, shiftDurationHours } from './format';
import ShiftPicker from './ShiftPicker';
import ColleaguePicker from './ColleaguePicker';

interface RequestSwapDialogProps {
  open: boolean;
  currentUserId: string;
  companyId: number;
  submitting: boolean;
  locale?: string;
  onClose: () => void;
  onSubmit: (input: ShiftSwapRequestInput) => void;
}

// The window we request. The server clamps this down to the company's configured horizon, so 60
// (the backend default) never over-fetches, but it stops the old 30-day cap from hiding valid
// day 31-60 shifts.
const HORIZON_DAYS = 60;

type SwapPath = 'trade' | 'cover';

const RequestSwapDialog: React.FC<RequestSwapDialogProps> = ({
  open,
  currentUserId,
  submitting,
  locale,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const [myShifts, setMyShifts] = useState<ScheduledShiftView[]>([]);
  const [loadingMyShifts, setLoadingMyShifts] = useState(false);
  const [myShiftsError, setMyShiftsError] = useState<string | null>(null);
  const [colleagues, setColleagues] = useState<SwapColleague[]>([]);
  const [loadingColleagues, setLoadingColleagues] = useState(false);
  const [colleaguesError, setColleaguesError] = useState<string | null>(null);
  const [colleagueShifts, setColleagueShifts] = useState<ScheduledShiftView[]>([]);
  const [loadingColleagueShifts, setLoadingColleagueShifts] = useState(false);

  // No default path (D2 parity with mobile): the user chooses Trade vs Give-away up front.
  const [path, setPath] = useState<SwapPath | null>(null);
  const [myShiftKey, setMyShiftKey] = useState<string>('');
  const [colleagueId, setColleagueId] = useState<string>('');
  const [counterpartyShiftKey, setCounterpartyShiftKey] = useState<string>('');
  const [reason, setReason] = useState('');
  const pathOptionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const isTrade = path === 'trade';

  const range = useMemo(() => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date(Date.now() + HORIZON_DAYS * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  // Reset everything each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setPath(null);
    setMyShiftKey('');
    setColleagueId('');
    setCounterpartyShiftKey('');
    setReason('');
    setColleagueShifts([]);
    setMyShiftsError(null);
    setColleaguesError(null);
  }, [open]);

  // Hoisted so the inline error blocks can re-invoke them via a Retry button.
  const loadShifts = useCallback(async () => {
    setLoadingMyShifts(true);
    setMyShiftsError(null);
    try {
      const res = await getSwapEligibleShifts(currentUserId, range.from, range.to);
      if (res.errors.length > 0) {
        setMyShiftsError(getApiErrorMessage(res, t, t('Could not load your shifts')));
      } else {
        setMyShifts(res.payload);
      }
    } catch (err) {
      setMyShiftsError(getApiErrorMessage(err, t, t('Could not load your shifts')));
    }
    setLoadingMyShifts(false);
  }, [currentUserId, range.from, range.to, t]);

  const loadColleagues = useCallback(async () => {
    setLoadingColleagues(true);
    setColleaguesError(null);
    try {
      const res = await getSwappableColleagues();
      if (res.errors.length > 0) {
        setColleaguesError(getApiErrorMessage(res, t, t('Could not load colleagues')));
      } else {
        setColleagues(res.payload);
      }
    } catch (err) {
      setColleaguesError(getApiErrorMessage(err, t, t('Could not load colleagues')));
    }
    setLoadingColleagues(false);
  }, [t]);

  // Load my shifts + colleagues on open.
  useEffect(() => {
    if (!open) return;
    loadShifts();
    loadColleagues();
  }, [open, loadShifts, loadColleagues]);

  // Load the colleague's shifts when one is picked for a two-way (trade) swap.
  const loadColleagueShifts = useCallback(async (userId: string) => {
    setLoadingColleagueShifts(true);
    setColleagueShifts([]);
    try {
      const res = await getSwapEligibleShifts(userId, range.from, range.to);
      if (res.errors.length === 0) setColleagueShifts(res.payload);
    } catch { /* ignore */ }
    setLoadingColleagueShifts(false);
  }, [range.from, range.to]);

  useEffect(() => {
    if (open && isTrade && colleagueId) {
      loadColleagueShifts(colleagueId);
    }
  }, [open, isTrade, colleagueId, loadColleagueShifts]);

  // Resolve the chosen shifts by their UTC startTime (the ShiftPicker's key). Virtual occurrences
  // all carry id === 0, so keying by id would collapse every upcoming shift to one option; startTime
  // is unique per user and is exactly what the request payload sends.
  const selectedMyShift = useMemo(
    () => myShifts.find((s) => s.startTime === myShiftKey),
    [myShifts, myShiftKey]
  );
  const selectedCounterShift = useMemo(
    () => colleagueShifts.find((s) => s.startTime === counterpartyShiftKey),
    [colleagueShifts, counterpartyShiftKey]
  );

  // Client-side sanity check for two-way swaps: comparing the true instants (UTC)
  // catches identical or overlapping slots before hitting the server.
  const validationError = useMemo(() => {
    if (!isTrade || !selectedMyShift || !selectedCounterShift) return null;
    const aStart = new Date(selectedMyShift.startTime).getTime();
    const aEnd = new Date(selectedMyShift.endTime).getTime();
    const bStart = new Date(selectedCounterShift.startTime).getTime();
    const bEnd = new Date(selectedCounterShift.endTime).getTime();
    if (aStart === bStart && aEnd === bEnd) {
      return t('Both shifts cover the same time. Pick a different shift to swap.');
    }
    if (aStart < bEnd && bStart < aEnd) {
      return t('These shifts overlap in time, so you cannot work both.');
    }
    return null;
  }, [isTrade, selectedMyShift, selectedCounterShift, t]);

  // Net change in hours for a trade — turns the exchange into an informed decision.
  const hoursDelta = useMemo(() => {
    if (!isTrade || !selectedMyShift || !selectedCounterShift) return null;
    const mine = shiftDurationHours(selectedMyShift.startTime, selectedMyShift.endTime);
    const theirs = shiftDurationHours(selectedCounterShift.startTime, selectedCounterShift.endTime);
    if (mine == null || theirs == null) return null;
    return theirs - mine;
  }, [isTrade, selectedMyShift, selectedCounterShift]);

  const deltaText = useMemo(() => {
    if (hoursDelta == null) return null;
    const rounded = Math.round(hoursDelta * 10) / 10;
    if (Math.abs(rounded) < 0.05) return t('Same hours');
    const h = Math.abs(rounded);
    return rounded > 0 ? t('You work {{h}}h more', { h }) : t('You work {{h}}h less', { h });
  }, [hoursDelta, t]);

  const counterpartyShiftMissing = isTrade && !selectedCounterShift;
  const canSubmit =
    !submitting &&
    path !== null &&
    !!selectedMyShift &&
    !!colleagueId &&
    !counterpartyShiftMissing &&
    !validationError;

  const showPreview = path !== null && !!selectedMyShift && (!isTrade || !!selectedCounterShift);
  const giveSummary = selectedMyShift
    ? formatLocalShiftSummary(selectedMyShift.localStartTime ?? selectedMyShift.startTime, selectedMyShift.localEndTime ?? selectedMyShift.endTime, locale)
    : '';
  const receiveSummary = selectedCounterShift
    ? formatLocalShiftSummary(selectedCounterShift.localStartTime ?? selectedCounterShift.startTime, selectedCounterShift.localEndTime ?? selectedCounterShift.endTime, locale)
    : '';

  const handleSubmit = () => {
    if (!selectedMyShift || !colleagueId || !canSubmit) return;
    const counterShift = isTrade ? selectedCounterShift : undefined;

    onSubmit({
      requesterShiftStartTime: selectedMyShift.startTime,
      requesterShiftEndTime: selectedMyShift.endTime,
      counterpartyUserId: colleagueId,
      counterpartyShiftStartTime: counterShift ? counterShift.startTime : null,
      counterpartyShiftEndTime: counterShift ? counterShift.endTime : null,
      reason: reason.trim() || null,
    });
  };

  const noShiftsEmpty = !loadingMyShifts && !myShiftsError && myShifts.length === 0;

  const pathOptions: { key: SwapPath; icon: typeof ArrowLeftRight; title: string; desc: string }[] = [
    { key: 'trade', icon: ArrowLeftRight, title: t('Trade'), desc: t('Swap your shift for one of their shifts.') },
    { key: 'cover', icon: ArrowRight, title: t('Give away cover'), desc: t('A colleague covers your shift. You get nothing back.') },
  ];
  const selectedPathIndex = pathOptions.findIndex((opt) => opt.key === path);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onClose(); }}>
      <DialogContent
        size="lg"
        onEscapeKeyDown={(e) => { if (submitting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (submitting) e.preventDefault(); }}
      >
        {/* Sticky so the title stays anchored while the form scrolls on short viewports.
            No z-index: the header must paint above the scrolling body (it's positioned, they're
            not) yet stay below the absolutely-positioned close button that follows it in the DOM. */}
        <DialogHeader className="sticky top-0 bg-card">
          <DialogTitle>{t('Request a shift swap')}</DialogTitle>
          <DialogDescription>
            {t('Offer one of your upcoming shifts to a colleague for approval.')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          {myShiftsError ? (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-destructive/40 bg-destructive/5 py-8 text-center">
              <AlertTriangle size={26} className="text-destructive" />
              <p className="max-w-xs text-sm font-medium text-foreground">{myShiftsError}</p>
              <Button variant="outline" size="sm" onClick={loadShifts} data-testid="swap-my-shifts-retry">
                <RefreshCw size={14} className="me-1.5" />
                {t('Try again')}
              </Button>
            </div>
          ) : noShiftsEmpty ? (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-8 text-center">
              <CalendarX2 size={28} className="text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">{t('No upcoming shifts to swap')}</p>
              <p className="max-w-xs text-xs text-muted-foreground">
                {t('You need a scheduled shift in the next {{days}} days before you can request a swap.', { days: HORIZON_DAYS })}
              </p>
            </div>
          ) : (
            <>
              {/* Path: Trade vs Give-away cover (no default) */}
              <div>
                <label className="mb-2 block text-sm font-medium text-foreground">{t('What do you want to do?')}</label>
                <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('Swap type')}>
                  {pathOptions.map((opt, i) => {
                    const selected = path === opt.key;
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={submitting}
                        tabIndex={i === (selectedPathIndex >= 0 ? selectedPathIndex : 0) ? 0 : -1}
                        ref={(el) => { pathOptionRefs.current[i] = el; }}
                        onClick={() => { setPath(opt.key); setCounterpartyShiftKey(''); }}
                        onKeyDown={(e) => {
                          const move = (to: number) => {
                            const n = pathOptions.length;
                            if (n === 0) return;
                            const next = ((to % n) + n) % n;
                            setPath(pathOptions[next].key);
                            setCounterpartyShiftKey('');
                            pathOptionRefs.current[next]?.focus();
                          };
                          // Horizontal grid: Left/Right are primary, mirrored under RTL.
                          const rtl = (document.documentElement.dir || document.dir) === 'rtl';
                          switch (e.key) {
                            case 'ArrowRight':
                              e.preventDefault();
                              move(rtl ? i - 1 : i + 1);
                              break;
                            case 'ArrowLeft':
                              e.preventDefault();
                              move(rtl ? i + 1 : i - 1);
                              break;
                            case 'ArrowDown':
                              e.preventDefault();
                              move(i + 1);
                              break;
                            case 'ArrowUp':
                              e.preventDefault();
                              move(i - 1);
                              break;
                            case 'Home':
                              e.preventDefault();
                              move(0);
                              break;
                            case 'End':
                              e.preventDefault();
                              move(pathOptions.length - 1);
                              break;
                            default:
                              break;
                          }
                        }}
                        data-testid={`swap-path-${opt.key}`}
                        className={`flex flex-col gap-1 rounded-lg border-2 p-3 text-start transition-colors disabled:opacity-50 ${
                          selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                        }`}
                      >
                        <span className={`inline-flex items-center gap-2 text-sm font-semibold ${selected ? 'text-primary' : 'text-foreground'}`}>
                          <Icon size={16} className={opt.key === 'cover' ? 'rtl:-scale-x-100' : ''} />
                          {opt.title}
                        </span>
                        <span className="text-xs text-muted-foreground">{opt.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* The rest is disclosed only after a path is chosen. */}
              {path !== null && (
                <>
                  {/* My shift */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-foreground">{t('Your shift')}</label>
                    <ShiftPicker
                      shifts={myShifts}
                      value={myShiftKey}
                      onChange={setMyShiftKey}
                      loading={loadingMyShifts}
                      disabled={submitting}
                      locale={locale}
                      emptyLabel={t('No upcoming shifts to swap')}
                      testId="swap-my-shift-picker"
                    />
                  </div>

                  {/* Colleague */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-foreground">{t('Colleague')}</label>
                    {colleaguesError ? (
                      <div className="flex items-center justify-between gap-2 rounded-lg border border-dashed border-destructive/40 bg-destructive/5 px-3 py-2.5 text-xs text-foreground">
                        <span className="min-w-0">{colleaguesError}</span>
                        <button
                          type="button"
                          onClick={loadColleagues}
                          className="shrink-0 font-medium text-primary hover:underline"
                          data-testid="swap-colleagues-retry"
                        >
                          {t('Try again')}
                        </button>
                      </div>
                    ) : (
                      <ColleaguePicker
                        colleagues={colleagues}
                        value={colleagueId}
                        onChange={(id) => { setColleagueId(id); setCounterpartyShiftKey(''); }}
                        loading={loadingColleagues}
                        disabled={submitting}
                        testId="swap-colleague-picker"
                      />
                    )}
                  </div>

                  {/* Counterparty shift (trade only) */}
                  {isTrade && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-foreground">
                        {t('Shift you want in return')}
                      </label>
                      {!colleagueId ? (
                        <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground">
                          {t('Select a colleague first to see their shifts.')}
                        </p>
                      ) : (
                        <ShiftPicker
                          shifts={colleagueShifts}
                          value={counterpartyShiftKey}
                          onChange={setCounterpartyShiftKey}
                          loading={loadingColleagueShifts}
                          disabled={submitting}
                          locale={locale}
                          emptyLabel={t('This colleague has no upcoming shifts to swap. Choose "Give away cover" above to ask them to cover your shift instead.')}
                          testId="swap-their-shift-picker"
                        />
                      )}
                    </div>
                  )}

                  {/* Reason */}
                  <div>
                    <label htmlFor="swap-reason" className="mb-2 block text-sm font-medium text-foreground">
                      {t('Reason')} <span className="font-normal text-muted-foreground">{t('(optional)')}</span>
                    </label>
                    <textarea
                      id="swap-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={3}
                      disabled={submitting}
                      placeholder={t('Add context for your colleague and manager')}
                      data-testid="swap-request-reason"
                      className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                    />
                  </div>

                  {/* Validation warning (two-way overlap / identical slots) */}
                  {validationError && (
                    <div
                      role="alert"
                      className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                      data-testid="swap-validation-warning"
                    >
                      <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                      <span>{validationError}</span>
                    </div>
                  )}

                </>
              )}
            </>
          )}
        </DialogBody>

        {/* "You give / You receive" summary — docked as a sticky bar between the scrolling body
            and the footer so the core of the exchange stays in view while the form scrolls on
            short viewports. bg-card is opaque so fields scroll invisibly behind it. */}
        {showPreview && (
          <div
            className="sticky bottom-0 border-t border-border bg-card px-6 py-3 text-sm"
            data-testid="swap-request-preview"
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-muted-foreground">{t('You give')}</span>
              <span className="font-medium text-foreground tabular-nums">{giveSummary}</span>
              <span className="text-muted-foreground" aria-hidden="true">&middot;</span>
              <span className="text-muted-foreground">{t('You receive')}</span>
              <span className="font-medium text-foreground tabular-nums">
                {isTrade ? receiveSummary : t('Nothing (one-way cover)')}
              </span>
            </div>
            {deltaText && <p className="mt-1 text-xs text-muted-foreground">{deltaText}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" disabled={submitting} onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="default"
            size="sm"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="swap-request-submit"
          >
            <ArrowLeftRight size={15} className="me-1.5" />
            {t('Send request')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequestSwapDialog;
